import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AlertCircle, Calculator, CheckCircle, Loader, RefreshCw, Save } from 'lucide-react';
import Layout from '../components/Layout';
import { RootState } from '../store';
import { setProjects } from '../store/projectsSlice';
import { projectsApi, taxFactsApi, extractErrorMessage } from '../services/api';

interface TaxFactValue {
  id: number;
  field_key: string;
  label: string;
  value: string | number | boolean | null;
  value_type: string;
  source_type: string;
  source_ref?: string | null;
  confidence?: number | null;
  verification_status: string;
  occurrence_key: string;
  updated_at?: string | null;
}

interface TaxFactGroup {
  section: string;
  label: string;
  values: TaxFactValue[];
}

interface TaxFactIssue {
  id: number;
  severity: 'info' | 'warning' | 'error';
  message: string;
  issueType: string;
}

interface TaxFactsResponse {
  factSet: {
    id: number;
    projectId: number;
    taxYear: number;
    status: string;
    updatedAt?: string | null;
  };
  groups: TaxFactGroup[];
  issues: TaxFactIssue[];
}

const sourceStyles: Record<string, string> = {
  document: 'bg-blue-50 text-blue-700 border-blue-200',
  questionnaire: 'bg-amber-50 text-amber-700 border-amber-200',
  user: 'bg-green-50 text-green-700 border-green-200',
  inferred: 'bg-gray-50 text-gray-600 border-gray-200',
};

function displayValue(value: TaxFactValue['value']) {
  if (value === null || value === undefined || value === '') return '';
  return String(value);
}

export default function TaxInputs() {
  const dispatch = useDispatch();
  const { projects, activeProjectId } = useSelector((state: RootState) => state.projects);
  const activeProject = projects.find((project) => project.id === activeProjectId);
  const [facts, setFacts] = useState<TaxFactsResponse | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [recalculating, setRecalculating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (projects.length > 0) return;
    projectsApi.list()
      .then((res) => dispatch(setProjects(res.data)))
      .catch(() => {
        // Empty projects are handled by the page state.
      });
  }, [dispatch, projects.length]);

  useEffect(() => {
    if (!activeProjectId) return;
    setLoading(true);
    setError(null);
    taxFactsApi.getProjectFacts(activeProjectId)
      .then((res) => {
        const data = res.data as TaxFactsResponse;
        setFacts(data);
        const nextDrafts: Record<string, string> = {};
        data.groups.forEach((group) => {
          group.values.forEach((value) => {
            nextDrafts[`${value.field_key}::${value.occurrence_key}`] = displayValue(value.value);
          });
        });
        setDrafts(nextDrafts);
      })
      .catch((err) => setError(extractErrorMessage(err, 'Unable to load TaxFacts.')))
      .finally(() => setLoading(false));
  }, [activeProjectId]);

  const issueCounts = useMemo(() => {
    const counts = { error: 0, warning: 0, info: 0 };
    facts?.issues.forEach((issue) => {
      counts[issue.severity] += 1;
    });
    return counts;
  }, [facts]);

  const saveValue = async (value: TaxFactValue) => {
    if (!activeProjectId) return;
    const draftKey = `${value.field_key}::${value.occurrence_key}`;
    setSavingKey(draftKey);
    setError(null);
    try {
      const raw = drafts[draftKey] ?? '';
      const parsed = value.value_type === 'money' || value.value_type === 'number'
        ? Number(raw || 0)
        : raw;
      const res = await taxFactsApi.updateValues(activeProjectId, [{
        field_key: value.field_key,
        occurrence_key: value.occurrence_key,
        value: parsed,
        verification_status: 'user_corrected',
      }]);
      setFacts(res.data as TaxFactsResponse);
    } catch (err) {
      setError(extractErrorMessage(err, 'Unable to save TaxFacts value.'));
    } finally {
      setSavingKey(null);
    }
  };

  const recalculate = async () => {
    if (!activeProjectId) return;
    setRecalculating(true);
    setError(null);
    try {
      await taxFactsApi.recalculate(activeProjectId);
      const res = await taxFactsApi.getProjectFacts(activeProjectId);
      setFacts(res.data as TaxFactsResponse);
    } catch (err) {
      setError(extractErrorMessage(err, 'Unable to recalculate from TaxFacts.'));
    } finally {
      setRecalculating(false);
    }
  };

  return (
    <Layout>
      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tax Inputs</h1>
          <p className="mt-1 text-sm text-gray-500">
            {activeProject ? `${activeProject.name} · Tax Year ${activeProject.taxYear}` : 'Canonical values from documents and questionnaire answers'}
          </p>
        </div>
        <button
          onClick={recalculate}
          disabled={!facts || recalculating}
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-gray-900 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-gray-800 disabled:cursor-not-allowed disabled:bg-gray-300"
        >
          {recalculating ? <Loader className="h-4 w-4 animate-spin" /> : <Calculator className="h-4 w-4" />}
          <span>Recalculate</span>
        </button>
      </div>

      {!activeProjectId && (
        <div className="rounded-lg border border-gray-200 bg-white p-6 text-sm text-gray-600">
          Upload a document first so Filie can create a tax project for these inputs.
        </div>
      )}

      {error && (
        <div className="mb-4 flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <p>{error}</p>
        </div>
      )}

      {loading && (
        <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white p-6 text-sm text-gray-600">
          <Loader className="h-4 w-4 animate-spin" />
          <span>Loading canonical tax inputs...</span>
        </div>
      )}

      {facts && (
        <>
          <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <p className="text-xs uppercase tracking-wide text-gray-400">Errors</p>
              <p className="mt-1 text-2xl font-bold text-red-600">{issueCounts.error}</p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <p className="text-xs uppercase tracking-wide text-gray-400">Warnings</p>
              <p className="mt-1 text-2xl font-bold text-amber-600">{issueCounts.warning}</p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <p className="text-xs uppercase tracking-wide text-gray-400">Last Updated</p>
              <p className="mt-1 text-sm font-medium text-gray-900">{facts.factSet.updatedAt || 'Not yet'}</p>
            </div>
          </div>

          {facts.issues.length > 0 && (
            <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 p-4">
              <h2 className="mb-3 text-sm font-semibold text-amber-900">Fields To Review</h2>
              <div className="space-y-2">
                {facts.issues.map((issue) => (
                  <div key={issue.id} className="flex items-start gap-2 text-sm text-amber-800">
                    <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                    <span>{issue.message}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-6">
            {facts.groups.map((group) => (
              <section key={group.section} className="rounded-lg border border-gray-200 bg-white">
                <div className="border-b border-gray-100 px-5 py-4">
                  <h2 className="text-base font-semibold text-gray-900">{group.label}</h2>
                </div>
                <div className="divide-y divide-gray-100">
                  {group.values.map((value) => {
                    const draftKey = `${value.field_key}::${value.occurrence_key}`;
                    const sourceClass = sourceStyles[value.source_type] || sourceStyles.inferred;
                    return (
                      <div key={`${value.field_key}-${value.occurrence_key}`} className="grid gap-3 px-5 py-4 lg:grid-cols-[1fr_220px_160px] lg:items-center">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{value.label}</p>
                          <p className="mt-0.5 text-xs text-gray-400">{value.field_key}</p>
                        </div>
                        <input
                          value={drafts[draftKey] ?? ''}
                          onChange={(event) => setDrafts((current) => ({ ...current, [draftKey]: event.target.value }))}
                          className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-gray-400 focus:outline-none"
                        />
                        <div className="flex items-center justify-between gap-2">
                          <span className={`inline-flex items-center rounded-full border px-2 py-1 text-xs font-medium ${sourceClass}`}>
                            {value.source_type.replace('_', ' ')}
                          </span>
                          <button
                            onClick={() => saveValue(value)}
                            disabled={savingKey === draftKey}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-900 disabled:opacity-50"
                            title="Save value"
                          >
                            {savingKey === draftKey ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                          </button>
                          {value.verification_status.includes('user') && <CheckCircle className="h-4 w-4 text-green-600" />}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        </>
      )}
    </Layout>
  );
}
